import { buildSourceTaggedUrl } from '@lib/sourceTaggedUrl';
import { getCommunityPath } from '@utils/slugHelpers';

/** Where an invite link is rendered — becomes its `?source=` for attribution.
 * `qr_invite` matches the analytics `qr_*` scan-detection prefix (D14). */
export type InviteLinkSource = 'qr_invite' | 'invite_link';

/** The four fields a space URL is actually built from. Deliberately narrower
 * than `CommunityRead` (which still satisfies it) so screens holding loose
 * props — SetupSpace's StepAccess — can call the builder with a typed literal
 * instead of casting a 4-field object to the full model.
 *
 * Only `slug` is optional. `CommunityRead` declares the other two as REQUIRED,
 * so every caller structurally carried them before this type existed, and a
 * caller who simply forgets `invite_token` gets no error from an optional field:
 * the link builds, silently without `?invite=`, and every recipient of the
 * unlisted space hits the 403 gate. (A MISSPELT key is caught either way, by
 * excess-property checking on the object literal.) */
export interface SpaceInviteLinkTarget {
  id: string;
  slug?: string | null;
  is_unlisted: boolean | null;
  invite_token: string | null;
}

/**
 * The one rule for when a space URL needs the invite token: unlisted spaces are
 * invisible/unjoinable without it; listed spaces ignore it server-side. Every
 * surface that puts a space URL in front of a user resolves the token here, so
 * the rule can't drift between the share sheet, the settings screen and the
 * address bar.
 */
export function spaceInviteTokenForUrl(
  space: { is_unlisted?: boolean | null; invite_token?: string | null } | null | undefined,
): string | null {
  return space?.is_unlisted && space.invite_token ? space.invite_token : null;
}

/**
 * Canonical "share this Space" URL — organizer surfaces only (admin invite
 * screen, activation drawers). Targets the space itself, exactly like a peer
 * share: onboarding is delivered by the entry redirect on landing, not by the
 * link's shape (see docs/onboarding/onboarding-design.md §2, §9, which
 * retires D17). `source` survives as attribution only.
 *
 * The token is folded into the PATH and the finished URL is assembled by
 * `buildSourceTaggedUrl` — the same call, on the same shape of input, that the
 * share sheet's QR makes with `context.path` (which `useShareSheet` builds the
 * same way). That is what keeps one Space + one token to one byte-identical
 * URL across both surfaces, and so to one cached QR image.
 */
export function buildSpaceInviteLink(
  community: SpaceInviteLinkTarget,
  source: InviteLinkSource,
): string {
  const token = spaceInviteTokenForUrl(community);
  const path = getCommunityPath(community);
  return buildSourceTaggedUrl(token ? `${path}?invite=${encodeURIComponent(token)}` : path, source);
}
